// web/src/types/common.ts - Shared/utility types, system settings, API types

// Partner integration key (client API key with is_partner_key=true)
export interface PartnerKey {
  id: number;
  client_id: number;
  client_name?: string;
  name: string;
  key_prefix: string;
  permissions: string;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  last_used_at?: string | null;
  last_used_ip?: string;
  total_requests: number;
  expires_at?: string | null;
  is_active: boolean;
  revoked_at?: string | null;
  revoke_reason?: string;
  created_by_id: number;
  created_at: string;
  updated_at: string;
  // Partner fields
  is_partner_key: boolean;
  environment: 'test' | 'prod';
  callback_url: string;
  callback_key_masked: string;
  source_platform: string;
  source_tenant_id: string;
  client?: { id: number; name: string };
}

export interface SystemSettings {
  id?: number;
  // Security & Access
  allow_registration: boolean;
  session_timeout_hours: number;
  require_email_verification: boolean;
  max_login_attempts: number;

  // System Limits
  max_concurrent_scans: number;
  allow_parallel_scheduled_scans: boolean;
  allow_parallel_manual_scans_project: boolean;
  max_targets_per_project: number;
  max_results_per_scan: number;
  scan_timeout_minutes: number;
  max_screenshots_per_scan: number;

  // Storage & Cleanup
  max_storage_gb: number;
  auto_cleanup_days: number;
  cleanup_screenshots: boolean;
  compress_old_scans: boolean;

  // Performance & Resource Management
  max_cpu_usage: number;
  max_memory_usage: number;
  scan_queue_size: number;

  // Notification & Alerting
  email_notifications: boolean;
  webhook_url: string;
  slack_webhook: string;

  // SMTP Email Configuration
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_from_email: string;
  smtp_from_name: string;
  smtp_use_tls: boolean;

  // External Tool Integration
  nmap_path: string;
  chrome_path: string;
  smbclient_path: string;

  // Simple Logging
  enable_logging: boolean;
  log_level: string;
  log_retention_days: number;

  // API & Rate Limiting
  api_rate_limit: number;
  enable_api_key: boolean;

  // Branding & Customization
  application_name: string;
  application_logo: string;
  custom_css: string;

  // Severity Color Configuration
  critical_color: string;
  critical_bg: string;
  critical_border: string;
  high_color: string;
  high_bg: string;
  high_border: string;
  medium_color: string;
  medium_bg: string;
  medium_border: string;
  low_color: string;
  low_bg: string;
  low_border: string;
  info_color: string;
  info_bg: string;
  info_border: string;
  system_color: string;
  system_bg: string;
  system_border: string;

  created_at?: string;
  updated_at?: string;
}

// Severity color types for the color context
export interface SeverityColorSet {
  color: string;
  bg: string;
  border: string;
}

export interface SeverityColors {
  critical: SeverityColorSet;
  high: SeverityColorSet;
  medium: SeverityColorSet;
  low: SeverityColorSet;
  info: SeverityColorSet;
  system: SeverityColorSet;
}

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'system';

// Default severity colors
export const DEFAULT_SEVERITY_COLORS: SeverityColors = {
  critical: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  high: { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
  medium: { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  low: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  info: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  system: { color: '#94a3b8', bg: '#f1f5f9', border: '#cbd5e1' },
};

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  error?: string;
}

export interface ScanControlResponse {
  message: string;
  status: string;
  scan_id: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Chart/Statistics types
export interface SeverityStats {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export interface EngineStats {
  nmap: number;
  web: number;
  ssl: number;
  shares: number;
  screenshots: number;
  vuln: number;
}

export interface ScanSummary {
  total_scans: number;
  running_scans: number;
  completed_scans: number;
  failed_scans: number;
  total_findings: number;
  severity_breakdown: SeverityStats;
  engine_breakdown: EngineStats;
}

// Vulnerability summary for target table
export interface VulnerabilitySummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  total: number;
}

// Pagination
export interface PaginationInfo {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// Error types
export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  message: string;
}

// Component prop types
export interface TableColumn<T> {
  key: keyof T;
  label: string;
  render?: (value: any, item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}

// Export utility types
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never;
}[keyof T];

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type WithTimestamps<T> = T & {
  created_at: string;
  updated_at: string;
};

// Default values
export const DEFAULT_VULNERABILITY_SUMMARY: VulnerabilitySummary = {
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  info: 0,
  total: 0,
};

export const DEFAULT_SYSTEM_SETTINGS: SystemSettings = {
  // Security & Access
  allow_registration: false,
  session_timeout_hours: 24,
  require_email_verification: false,
  max_login_attempts: 5,

  // System Limits
  max_concurrent_scans: 10,
  allow_parallel_scheduled_scans: true,
  allow_parallel_manual_scans_project: false,
  max_targets_per_project: 50,
  max_results_per_scan: 10000,
  scan_timeout_minutes: 60,
  max_screenshots_per_scan: 100,

  // Storage & Cleanup
  max_storage_gb: 10,
  auto_cleanup_days: 30,
  cleanup_screenshots: true,
  compress_old_scans: true,

  // Performance & Resource Management
  max_cpu_usage: 80,
  max_memory_usage: 70,
  scan_queue_size: 10,

  // Notification & Alerting
  email_notifications: false,
  webhook_url: '',
  slack_webhook: '',

  // SMTP Email Configuration
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: 'VulnScanner',
  smtp_use_tls: true,

  // External Tool Integration
  nmap_path: 'nmap',
  chrome_path: '',
  smbclient_path: 'smbclient',

  // Simple Logging
  enable_logging: true,
  log_level: 'info',
  log_retention_days: 7,

  // API & Rate Limiting
  api_rate_limit: 100,
  enable_api_key: false,

  // Branding & Customization
  application_name: 'VulnScanner',
  application_logo: '',
  custom_css: '',

  // Severity Color Configuration
  critical_color: '#dc2626',
  critical_bg: '#fef2f2',
  critical_border: '#fecaca',
  high_color: '#ea580c',
  high_bg: '#fff7ed',
  high_border: '#fed7aa',
  medium_color: '#d97706',
  medium_bg: '#fffbeb',
  medium_border: '#fde68a',
  low_color: '#16a34a',
  low_bg: '#f0fdf4',
  low_border: '#bbf7d0',
  info_color: '#2563eb',
  info_bg: '#eff6ff',
  info_border: '#bfdbfe',
  system_color: '#94a3b8',
  system_bg: '#f1f5f9',
  system_border: '#cbd5e1',
};
